/**
 * Forgot Password Page
 *
 * Sends a Supabase password-reset email. The link in that email opens
 * /auth/reset-password where the user sets a new password.
 *
 * Security: always shows the same message whether or not the email exists
 * (prevents account enumeration).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { getSupabaseClient } from '../../lib/supabaseClient';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Enter your email address.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (resetErr) {
        setError(resetErr.message || 'Something went wrong. Try again.');
        return;
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface)] dark:bg-[var(--bg)] px-4 transition-colors duration-300">
      <div className="w-full max-w-sm">
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-sm dark:shadow-none p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-consulting-slate-blue-100 dark:bg-[var(--active-tint)]">
              <Mail className="h-6 w-6 text-consulting-slate-blue-600 dark:text-[var(--text-ter)]" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Reset your password
              </h1>
              <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                We'll email you a link
              </p>
            </div>
          </div>

          {submitted ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)]/50 p-4">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>
                  If <strong>{email.trim().toLowerCase()}</strong> has an account, we sent a
                  password reset link. Check your inbox (and spam folder).
                </span>
              </div>
              <Link
                to="/auth/login"
                className="flex items-center gap-2 text-sm text-consulting-slate-blue-600 dark:text-[var(--text-ter)] hover:underline"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="no-dark w-full px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:ring-2 focus:ring-consulting-slate-blue-500 dark:focus:ring-[var(--active-tint)] focus:border-consulting-slate-blue-500 dark:focus:border-[var(--slate)] outline-none transition-all"
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-[var(--coral)] dark:text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)]/50 p-3">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-consulting-slate-blue-600 text-white font-medium hover:bg-consulting-slate-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  'Send reset link'
                )}
              </button>

              <Link
                to="/auth/login"
                className="flex items-center gap-2 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </form>
          )}
        </div>

        <div className="mt-4 flex justify-center">
          <ThemeToggle variant="labeled" />
        </div>
      </div>
    </div>
  );
}
